import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    AlertTriangle,
    Building2,
    Check,
    ChevronRight,
    ExternalLink,
    MapPin,
    Pencil,
    RefreshCw,
    Search,
    Sparkles,
    Store,
    Trash2,
    X,
} from "lucide-react";
import BONavigation from "../../../components/admin/BONavigation";
import {
    fetchAllWeirdNames,
    fetchWeirdNameRules,
    fetchWeirdNames,
    pappersCompanyUrl,
    pappersEstablishmentUrl,
    renameEstablishments,
    RENAME_CHUNK,
    SUGGESTION_LABELS,
    WeirdNameFilters,
    WeirdNameItem,
    WeirdNameRuleStat,
    SuggestionSource,
} from "../../../interfaces/weirdNames";

const LIMIT = 50;
const PREVIEW_CAP = 100; // rows drawn in the pending panel; a select-all can stage thousands

type Sort = "company" | "name" | "recent";
type Staged = { from: string; to: string };

const SOURCE_ACTIONS: { source: SuggestionSource; label: string }[] = [
    { source: "legal_name", label: "Raison sociale" },
    { source: "enseigne", label: "Enseigne" },
    { source: "sibling", label: "Autre établissement" },
    { source: "cleaned", label: "Nom nettoyé" },
];

function formatSiret(siret: string) {
    if (siret.length !== 14) return siret;
    return `${siret.slice(0, 3)} ${siret.slice(3, 6)} ${siret.slice(6, 9)} ${siret.slice(9)}`;
}

// ── Row ───────────────────────────────────────────────────────────────────────

function WeirdRow({
    item,
    ruleLabels,
    isSelected,
    staged,
    onToggle,
    onStage,
    onUnstage,
}: {
    item: WeirdNameItem;
    ruleLabels: Record<string, string>;
    isSelected: boolean;
    staged?: Staged;
    onToggle: (id: string) => void;
    onStage: (item: WeirdNameItem, name: string) => void;
    onUnstage: (id: string) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(staged?.to ?? item.name);

    const commit = () => {
        const value = draft.trim();
        setEditing(false);
        if (!value || value === item.name) {
            onUnstage(item.id);
            return;
        }
        onStage(item, value);
    };

    return (
        <div
            className={`px-3 py-2.5 transition-colors ${
                staged ? "bg-green-50/60" : isSelected ? "bg-black/[0.03]" : "hover:bg-[#F9F6F3]/60"
            }`}
        >
            <div className="flex items-start gap-3">
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggle(item.id)}
                    className="w-3.5 h-3.5 mt-1 rounded border-gray-300 accent-black cursor-pointer shrink-0"
                />

                <div className="flex-1 min-w-0">
                    {/* Name + what is wrong with it */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className={`text-xs font-semibold ${staged ? "text-gray-400 line-through" : "text-gray-900"}`}>
                            {item.name || <span className="italic text-gray-400">(sans nom)</span>}
                        </p>
                        {staged && (
                            <>
                                <ChevronRight className="w-3 h-3 text-green-500" />
                                <span className="text-xs font-semibold text-green-700">{staged.to}</span>
                                <button
                                    onClick={() => onUnstage(item.id)}
                                    title="Annuler ce renommage"
                                    className="text-green-500 hover:text-green-700"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </>
                        )}
                        {item.isSiege && (
                            <span className="px-1 py-0.5 rounded-sm bg-gray-100 text-[10px] text-gray-500 border border-gray-200">
                                siège
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {item.matchedRules.map(id => (
                            <span
                                key={id}
                                className="px-1.5 py-0.5 rounded-sm bg-red-50 text-[10px] font-medium text-red-600 border border-red-100"
                            >
                                {ruleLabels[id] ?? id}
                            </span>
                        ))}
                        {item.siret ? (
                            <a
                                href={pappersEstablishmentUrl(item.siret)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Voir cet établissement sur Pappers"
                                className="px-1.5 py-0.5 rounded-sm bg-gray-50 text-[10px] font-mono text-gray-600 border border-gray-200 hover:border-gray-400 hover:text-black flex items-center gap-1"
                            >
                                {formatSiret(item.siret)}
                                <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                        ) : (
                            <span className="px-1.5 py-0.5 rounded-sm bg-gray-50 text-[10px] text-gray-400 border border-gray-200">
                                sans SIRET
                            </span>
                        )}
                        {(item.city || item.postcode) && (
                            <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
                                <MapPin className="w-2.5 h-2.5" />
                                {[item.postcode, item.city].filter(Boolean).join(" ")}
                            </span>
                        )}
                        {item.legalName && (
                            <span className="text-[10px] text-gray-400 truncate max-w-[220px]">{item.legalName}</span>
                        )}
                    </div>

                    {/* One click stages a rename — nothing is written until the panel applies it */}
                    {item.suggestions.length > 0 && !editing && (
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            <Sparkles className="w-3 h-3 text-gray-300" />
                            {item.suggestions.map(s => (
                                <button
                                    key={`${s.source}-${s.value}`}
                                    onClick={() => onStage(item, s.value)}
                                    title={SUGGESTION_LABELS[s.source]}
                                    className="px-1.5 py-0.5 rounded-sm bg-white text-[10px] text-gray-700 border border-gray-200 hover:border-black hover:bg-black hover:text-white transition-colors"
                                >
                                    {s.value}
                                </button>
                            ))}
                        </div>
                    )}

                    {editing && (
                        <div className="flex items-center gap-1.5 mt-1.5">
                            <input
                                autoFocus
                                value={draft}
                                onChange={e => setDraft(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === "Enter") commit();
                                    if (e.key === "Escape") setEditing(false);
                                }}
                                className="flex-1 px-2 py-1 border border-gray-300 rounded-sm text-xs focus:outline-none focus:border-black"
                            />
                            <button
                                onClick={commit}
                                className="px-2 py-1 bg-black text-white rounded-sm text-[10px] font-semibold"
                            >
                                OK
                            </button>
                            <button
                                onClick={() => setEditing(false)}
                                className="px-2 py-1 border border-gray-200 rounded-sm text-[10px] text-gray-600"
                            >
                                Annuler
                            </button>
                        </div>
                    )}
                </div>

                <button
                    onClick={() => {
                        setDraft(staged?.to ?? item.name);
                        setEditing(v => !v);
                    }}
                    title="Renommer à la main"
                    className="p-1 rounded text-gray-300 hover:text-black hover:bg-gray-100 transition-colors shrink-0"
                >
                    <Pencil className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}

// ── Company group header ──────────────────────────────────────────────────────

function CompanyHeader({
    item,
    onFocusCompany,
    onSelectGroup,
}: {
    item: WeirdNameItem;
    onFocusCompany: (siren: string) => void;
    onSelectGroup: (siren: string) => void;
}) {
    const company = item.company;
    const siren = item.siren;

    return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F5F0EB] border-y border-gray-200 flex-wrap">
            <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span className="text-[11px] font-semibold text-gray-800 truncate max-w-[280px]">
                {company?.label || item.legalName || "Entreprise sans nom connu"}
            </span>
            {siren ? (
                <a
                    href={pappersCompanyUrl(siren)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Voir l'entreprise sur Pappers"
                    className="text-[10px] font-mono text-gray-500 hover:text-black flex items-center gap-1"
                >
                    SIREN {siren}
                    <ExternalLink className="w-2.5 h-2.5" />
                </a>
            ) : (
                <span className="text-[10px] text-gray-400">sans SIREN</span>
            )}
            {company && (
                <span
                    className={`px-1.5 py-0.5 rounded-sm text-[10px] font-medium border ${
                        company.weirdCount === company.total
                            ? "bg-red-50 text-red-600 border-red-100"
                            : "bg-orange-50 text-orange-600 border-orange-100"
                    }`}
                >
                    {company.weirdCount}/{company.total} à renommer
                </span>
            )}
            <div className="ml-auto flex items-center gap-2">
                {siren && (
                    <button
                        onClick={() => onSelectGroup(siren)}
                        className="text-[10px] text-gray-500 hover:text-black underline underline-offset-2"
                    >
                        Sélectionner
                    </button>
                )}
                {siren && (
                    <button
                        onClick={() => onFocusCompany(siren)}
                        className="text-[10px] text-gray-500 hover:text-black underline underline-offset-2"
                    >
                        Voir l'entreprise
                    </button>
                )}
            </div>
        </div>
    );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function WeirdNames() {
    const [ruleStats, setRuleStats] = useState<WeirdNameRuleStat[]>([]);
    const [ruleTotal, setRuleTotal] = useState<number | null>(null);
    const [rulesLoading, setRulesLoading] = useState(true);

    const [activeRules, setActiveRules] = useState<string[]>([]);
    const [query, setQuery] = useState("");
    const [custom, setCustom] = useState("");
    const [customApplied, setCustomApplied] = useState("");
    const [sirenFocus, setSirenFocus] = useState("");
    const [sort, setSort] = useState<Sort>("company");
    const [page, setPage] = useState(1);

    const [items, setItems] = useState<WeirdNameItem[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [staged, setStaged] = useState<Map<string, Staged>>(new Map());
    const [bulkName, setBulkName] = useState("");
    const [renameEvents, setRenameEvents] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveProgress, setSaveProgress] = useState<{ done: number; total: number } | null>(null);
    const [selectingAll, setSelectingAll] = useState(false);
    const [saveResult, setSaveResult] = useState<{ modified: number; events: number; rejected: number } | null>(
        null
    );
    const [notice, setNotice] = useState<string | null>(null);

    // Rows stay reachable for the bulk actions after the page they came from is gone.
    const knownItems = useRef<Map<string, WeirdNameItem>>(new Map());

    const ruleLabels = useMemo(
        () => Object.fromEntries(ruleStats.map(r => [r.id, r.label])) as Record<string, string>,
        [ruleStats]
    );

    const loadRules = useCallback(async (refresh = false) => {
        setRulesLoading(true);
        try {
            const data = await fetchWeirdNameRules(refresh);
            setRuleStats(data.rules);
            setRuleTotal(data.total);
        } catch (err: any) {
            setError(err?.message ?? "Erreur lors du comptage");
        } finally {
            setRulesLoading(false);
        }
    }, []);

    const filters: WeirdNameFilters = useMemo(
        () => ({
            rules: activeRules,
            q: query.trim() || undefined,
            custom: customApplied || undefined,
            siren: sirenFocus || undefined,
            sort,
        }),
        [activeRules, query, customApplied, sirenFocus, sort]
    );

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchWeirdNames({
                ...filters,
                page,
                limit: LIMIT,
            });
            setItems(data.items);
            setTotal(data.total);
            setTotalPages(data.totalPages);
            data.items.forEach(i => knownItems.current.set(i.id, i));
        } catch (err: any) {
            setError(err?.message ?? "Erreur lors du chargement");
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [filters, page]);

    useEffect(() => {
        loadRules();
    }, [loadRules]);

    useEffect(() => {
        const timer = setTimeout(load, 300);
        return () => clearTimeout(timer);
    }, [load]);

    // Every filter change restarts at page 1 — except a page change itself.
    const resetToFirstPage = () => setPage(1);

    const toggleRule = (id: string) => {
        resetToFirstPage();
        setActiveRules(prev => (prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]));
    };

    const toggleOne = (id: string) =>
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const pageIds = items.map(i => i.id);
    const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));

    const toggleSelectAll = () =>
        setSelected(prev => {
            const next = new Set(prev);
            if (allPageSelected) pageIds.forEach(id => next.delete(id));
            else pageIds.forEach(id => next.add(id));
            return next;
        });

    const selectGroup = (siren: string) =>
        setSelected(prev => {
            const next = new Set(prev);
            items.filter(i => i.siren === siren).forEach(i => next.add(i.id));
            return next;
        });

    /**
     * Everything matching the current filters, not just this page. Full rows are
     * fetched, not ids: "reprendre la raison sociale" needs each establishment's own
     * suggestions, so a selection made of bare ids could not take that action.
     */
    const selectAll = async () => {
        setSelectingAll(true);
        setError(null);
        try {
            const data = await fetchAllWeirdNames(filters);
            data.items.forEach(i => knownItems.current.set(i.id, i));
            setSelected(new Set(data.items.map(i => i.id)));
            setNotice(
                data.truncated
                    ? `${data.items.length.toLocaleString("fr-FR")} sélectionnés sur ${data.total.toLocaleString(
                          "fr-FR"
                      )} — plafond atteint, affinez le filtre pour le reste`
                    : `${data.items.length.toLocaleString("fr-FR")} établissement(s) sélectionné(s), toutes pages`
            );
        } catch (err: any) {
            setError(err?.message ?? "Erreur lors de la sélection");
        } finally {
            setSelectingAll(false);
        }
    };

    const focusCompany = (siren: string) => {
        setSirenFocus(siren);
        setCustom("");
        setCustomApplied("");
        setQuery("");
        resetToFirstPage();
    };

    const stage = (item: WeirdNameItem, name: string) =>
        setStaged(prev => {
            const next = new Map(prev);
            if (!name.trim() || name.trim() === item.name) next.delete(item.id);
            else next.set(item.id, { from: item.name, to: name.trim() });
            return next;
        });

    const unstage = (id: string) =>
        setStaged(prev => {
            const next = new Map(prev);
            next.delete(id);
            return next;
        });

    /** Same name on every selected row — only ever right inside one company. */
    const applyBulkName = () => {
        const name = bulkName.trim();
        if (!name || selected.size === 0) return;
        const picked: [string, Staged][] = [];
        selected.forEach(id => {
            const item = knownItems.current.get(id);
            if (item) picked.push([id, { from: item.name, to: name }]);
        });

        setStaged(prev => new Map([...Array.from(prev), ...picked]));
        setNotice(`${picked.length} renommage(s) préparé(s) vers « ${name} »`);
    };

    /** Each row takes its own value for that field — the usual fix for a whole company. */
    const applySource = (source: SuggestionSource) => {
        if (selected.size === 0) return;
        const picked: [string, Staged][] = [];
        let missing = 0;
        selected.forEach(id => {
            const item = knownItems.current.get(id);
            if (!item) return;
            const suggestion = item.suggestions.find(s => s.source === source);
            if (!suggestion) {
                missing++;
                return;
            }
            picked.push([id, { from: item.name, to: suggestion.value }]);
        });

        setStaged(prev => new Map([...Array.from(prev), ...picked]));
        setNotice(
            missing > 0
                ? `${picked.length} renommage(s) préparé(s) · ${missing} sans valeur pour ce champ`
                : `${picked.length} renommage(s) préparé(s)`
        );
    };

    const save = async () => {
        if (staged.size === 0) return;
        const payload = Array.from(staged.entries()).map(([id, s]) => ({ id, name: s.to }));
        const chunks: typeof payload[] = [];
        for (let i = 0; i < payload.length; i += RENAME_CHUNK) {
            chunks.push(payload.slice(i, i + RENAME_CHUNK));
        }

        setSaving(true);
        setSaveResult(null);
        setError(null);
        setSaveProgress({ done: 0, total: payload.length });

        let modified = 0;
        let events = 0;
        let rejected = 0;
        try {
            // Sequential on purpose: a batch of thousands must not fire fifty writes at
            // once, and a failure half-way still leaves the earlier chunks applied and
            // counted rather than silently lost.
            for (const chunk of chunks) {
                const result = await renameEstablishments(chunk, renameEvents);
                modified += result.modifiedCount;
                events += result.eventsModified ?? 0;
                rejected += result.rejected.length;
                setSaveProgress(prev => ({ done: (prev?.done ?? 0) + chunk.length, total: payload.length }));
            }
            setStaged(new Map());
            setSelected(new Set());
            setBulkName("");
            setNotice(null);
            knownItems.current.clear();
            await Promise.all([load(), loadRules(true)]);
        } catch (err: any) {
            setError(err?.message ?? "Erreur lors du renommage");
        } finally {
            setSaveResult({ modified, events, rejected });
            setSaveProgress(null);
            setSaving(false);
        }
    };

    // Consecutive rows of one company become one block — the list is sorted by SIREN.
    const groups = useMemo(() => {
        if (sort !== "company") return [{ key: "flat", head: null as WeirdNameItem | null, rows: items }];
        const out: { key: string; head: WeirdNameItem | null; rows: WeirdNameItem[] }[] = [];
        items.forEach(item => {
            const last = out[out.length - 1];
            if (last && last.key === (item.siren || "—")) last.rows.push(item);
            else out.push({ key: item.siren || "—", head: item, rows: [item] });
        });
        return out;
    }, [items, sort]);

    const stagedList = Array.from(staged.entries());
    const inCustomMode = !!customApplied;
    const inCompanyMode = !!sirenFocus;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                <div className="mb-6">
                    <h1 className="text-xl font-semibold text-black">Noms bizarres</h1>
                    <p className="text-xs text-gray-600 mt-0.5">
                        Les établissements dont le nom vient d'un code interne, d'un identifiant ou d'un
                        bouche-trou — à renommer, si possible d'après leur entreprise.
                    </p>
                </div>

                <BONavigation />

                <div className="flex gap-6">
                    {/* ── Left: filters + list ─────────────────────────────── */}
                    <div className="flex-1 min-w-0">
                        {/* Rule chips */}
                        <div className="border border-gray-200 rounded-sm bg-white p-3 mb-4">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                                    Motifs détectés
                                </span>
                                {ruleTotal !== null && (
                                    <span className="text-[10px] text-gray-400">
                                        {ruleTotal.toLocaleString("fr-FR")} établissement(s) au total
                                    </span>
                                )}
                                <button
                                    onClick={() => loadRules(true)}
                                    disabled={rulesLoading}
                                    title="Recompter (analyse complète de la base)"
                                    className="ml-auto p-1 rounded text-gray-400 hover:text-black hover:bg-gray-100 disabled:opacity-40"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${rulesLoading ? "animate-spin" : ""}`} />
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                <button
                                    onClick={() => {
                                        setActiveRules([]);
                                        resetToFirstPage();
                                    }}
                                    className={`px-2 py-1 rounded-sm text-[11px] font-medium border transition-colors ${
                                        activeRules.length === 0
                                            ? "bg-black text-white border-black"
                                            : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                                    }`}
                                >
                                    Tous
                                </button>
                                {ruleStats.map(rule => {
                                    const active = activeRules.includes(rule.id);
                                    return (
                                        <button
                                            key={rule.id}
                                            onClick={() => toggleRule(rule.id)}
                                            title={rule.description}
                                            className={`px-2 py-1 rounded-sm text-[11px] font-medium border transition-colors flex items-center gap-1.5 ${
                                                active
                                                    ? "bg-black text-white border-black"
                                                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                                            }`}
                                        >
                                            {rule.label}
                                            <span
                                                className={`px-1 rounded-sm text-[10px] font-semibold ${
                                                    active ? "bg-white/20" : "bg-gray-100 text-gray-500"
                                                }`}
                                            >
                                                {rule.count.toLocaleString("fr-FR")}
                                            </span>
                                        </button>
                                    );
                                })}
                                {rulesLoading && ruleStats.length === 0 && (
                                    <span className="text-[11px] text-gray-400">Comptage en cours…</span>
                                )}
                            </div>
                        </div>

                        {/* Search / free pattern / sort */}
                        <div className="flex flex-wrap gap-2 mb-3">
                            <div className="relative flex-1 min-w-[220px]">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    value={query}
                                    onChange={e => {
                                        setQuery(e.target.value);
                                        resetToFirstPage();
                                    }}
                                    placeholder="Filtrer : nom, ville, SIRET…"
                                    className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-sm text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-400"
                                />
                                {query && (
                                    <button
                                        onClick={() => setQuery("")}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                            <div className="relative flex-1 min-w-[220px]">
                                <input
                                    value={custom}
                                    onChange={e => setCustom(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key !== "Enter") return;
                                        setCustomApplied(custom.trim());
                                        setSirenFocus("");
                                        resetToFirstPage();
                                    }}
                                    placeholder='Motif libre, ex. "CODE INTERNE" puis Entrée'
                                    className="w-full px-3 py-2 border border-gray-200 rounded-sm text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-400"
                                />
                            </div>
                            <select
                                value={sort}
                                onChange={e => {
                                    setSort(e.target.value as Sort);
                                    resetToFirstPage();
                                }}
                                className="px-3 py-2 border border-gray-200 rounded-sm text-xs text-gray-700 bg-white focus:outline-none focus:border-gray-400"
                            >
                                <option value="company">Par entreprise</option>
                                <option value="name">Par nom</option>
                                <option value="recent">Plus récents</option>
                            </select>
                        </div>

                        {/* Active mode banner */}
                        {(inCustomMode || inCompanyMode) && (
                            <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-white border border-gray-200 rounded-sm">
                                <span className="text-[11px] text-gray-600">
                                    {inCompanyMode ? (
                                        <>
                                            Tous les établissements du SIREN{" "}
                                            <span className="font-mono font-semibold">{sirenFocus}</span>, bien
                                            nommés compris
                                        </>
                                    ) : (
                                        <>
                                            Recherche libre sur « <span className="font-semibold">{customApplied}</span> »
                                            — les motifs sont ignorés
                                        </>
                                    )}
                                </span>
                                <button
                                    onClick={() => {
                                        setSirenFocus("");
                                        setCustomApplied("");
                                        setCustom("");
                                        resetToFirstPage();
                                    }}
                                    className="ml-auto text-[11px] text-gray-500 hover:text-black underline underline-offset-2"
                                >
                                    Revenir aux motifs
                                </button>
                            </div>
                        )}

                        {error && (
                            <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-sm">
                                <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                                <span className="text-xs text-red-700">{error}</span>
                            </div>
                        )}

                        {/* List */}
                        <div className="border border-gray-200 rounded-sm overflow-hidden bg-white">
                            <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 border-b border-gray-200">
                                <input
                                    type="checkbox"
                                    checked={allPageSelected}
                                    onChange={toggleSelectAll}
                                    className="w-3.5 h-3.5 rounded border-gray-300 accent-black cursor-pointer"
                                />
                                <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                                    {loading
                                        ? "Chargement…"
                                        : `${total.toLocaleString("fr-FR")} établissement${total > 1 ? "s" : ""}`}
                                </span>
                                {total > items.length && (
                                    <button
                                        onClick={selectAll}
                                        disabled={selectingAll || loading}
                                        className="text-[11px] text-gray-500 hover:text-black underline underline-offset-2 disabled:opacity-40 disabled:no-underline"
                                    >
                                        {selectingAll
                                            ? "Sélection…"
                                            : `Tout sélectionner (${total.toLocaleString("fr-FR")})`}
                                    </button>
                                )}
                                <span className="flex-1" />
                                {selected.size > 0 && (
                                    <>
                                        <button
                                            onClick={() => setSelected(new Set())}
                                            className="text-[11px] text-gray-400 hover:text-black underline underline-offset-2"
                                        >
                                            Désélectionner
                                        </button>
                                        <span className="text-[11px] font-semibold text-black bg-black/5 px-2 py-0.5 rounded-sm">
                                            {selected.size.toLocaleString("fr-FR")} sélectionné
                                            {selected.size > 1 ? "s" : ""}
                                        </span>
                                    </>
                                )}
                            </div>

                            {loading ? (
                                <div className="divide-y divide-gray-100">
                                    {Array.from({ length: 8 }).map((_, i) => (
                                        <div key={i} className="flex items-center gap-3 px-3 py-3 animate-pulse">
                                            <div className="w-3.5 h-3.5 bg-gray-100 rounded shrink-0" />
                                            <div className="flex-1 space-y-1.5">
                                                <div className="h-3.5 bg-gray-100 rounded w-2/3" />
                                                <div className="h-2.5 bg-gray-100 rounded w-1/3" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : items.length === 0 ? (
                                <div className="py-16 text-center">
                                    <Store className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                                    <p className="text-sm text-gray-400">Aucun établissement pour ces critères</p>
                                </div>
                            ) : (
                                <div>
                                    {groups.map(group => (
                                        <div key={group.key}>
                                            {group.head && sort === "company" && (
                                                <CompanyHeader
                                                    item={group.head}
                                                    onFocusCompany={focusCompany}
                                                    onSelectGroup={selectGroup}
                                                />
                                            )}
                                            <div className="divide-y divide-gray-100">
                                                {group.rows.map(item => (
                                                    <WeirdRow
                                                        key={item.id}
                                                        item={item}
                                                        ruleLabels={ruleLabels}
                                                        isSelected={selected.has(item.id)}
                                                        staged={staged.get(item.id)}
                                                        onToggle={toggleOne}
                                                        onStage={stage}
                                                        onUnstage={unstage}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 my-4">
                                <button
                                    onClick={() => {
                                        setPage(p => Math.max(1, p - 1));
                                        window.scrollTo({ top: 0, behavior: "smooth" });
                                    }}
                                    disabled={page <= 1}
                                    className="px-3 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 bg-white hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Précédent
                                </button>
                                <span className="text-xs text-gray-500">
                                    Page {page} / {totalPages}
                                </span>
                                <button
                                    onClick={() => {
                                        setPage(p => Math.min(totalPages, p + 1));
                                        window.scrollTo({ top: 0, behavior: "smooth" });
                                    }}
                                    disabled={page >= totalPages}
                                    className="px-3 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 bg-white hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Suivant
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ── Right: rename panel ──────────────────────────────── */}
                    <div className="w-80 shrink-0">
                        <div className="sticky top-4 border border-gray-200 rounded-sm p-4 bg-white max-h-[calc(100vh-2rem)] overflow-y-auto">
                            <div className="flex items-center gap-2 mb-4">
                                <Pencil className="w-4 h-4 text-gray-500" />
                                <span className="text-sm font-semibold text-gray-900">Renommer</span>
                            </div>

                            <div
                                className={`mb-4 p-2.5 rounded-sm text-xs ${
                                    selected.size > 0
                                        ? "bg-black text-white"
                                        : "bg-gray-50 text-gray-500 border border-gray-200"
                                }`}
                            >
                                {selected.size > 0 ? (
                                    <span className="font-semibold">
                                        {selected.size} établissement{selected.size > 1 ? "s" : ""} sélectionné
                                        {selected.size > 1 ? "s" : ""}
                                    </span>
                                ) : (
                                    "Cochez des établissements, ou cliquez directement une suggestion sur une ligne"
                                )}
                            </div>

                            {/* Same name for everyone */}
                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                Même nom pour tous
                            </p>
                            <div className="flex gap-1.5 mb-1">
                                <input
                                    value={bulkName}
                                    onChange={e => setBulkName(e.target.value)}
                                    onKeyDown={e => e.key === "Enter" && applyBulkName()}
                                    placeholder="Nouveau nom"
                                    className="flex-1 min-w-0 px-2 py-1.5 border border-gray-200 rounded-sm text-xs focus:outline-none focus:border-black"
                                />
                                <button
                                    onClick={applyBulkName}
                                    disabled={!bulkName.trim() || selected.size === 0}
                                    className="px-2.5 py-1.5 bg-black text-white rounded-sm text-[11px] font-semibold disabled:bg-gray-100 disabled:text-gray-400"
                                >
                                    Préparer
                                </button>
                            </div>
                            <p className="text-[10px] text-gray-400 mb-4">
                                À réserver aux établissements d'une même enseigne.
                            </p>

                            {/* Per-row source */}
                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                Reprendre un champ existant
                            </p>
                            <div className="flex flex-wrap gap-1.5 mb-1">
                                {SOURCE_ACTIONS.map(action => (
                                    <button
                                        key={action.source}
                                        onClick={() => applySource(action.source)}
                                        disabled={selected.size === 0}
                                        className="px-2 py-1 border border-gray-200 rounded-sm text-[11px] text-gray-700 hover:border-black hover:bg-black hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-gray-700 disabled:hover:border-gray-200"
                                    >
                                        {action.label}
                                    </button>
                                ))}
                            </div>
                            <p className="text-[10px] text-gray-400 mb-4">
                                Chaque ligne prend sa propre valeur pour ce champ.
                            </p>

                            {notice && <p className="text-[10px] text-gray-500 mb-3">{notice}</p>}

                            {/* Pending renames */}
                            {stagedList.length > 0 && (
                                <div className="mb-4 border border-gray-200 rounded-sm">
                                    <div className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 border-b border-gray-200">
                                        <span className="text-[11px] font-semibold text-gray-600">
                                            {stagedList.length} renommage{stagedList.length > 1 ? "s" : ""} en attente
                                        </span>
                                        <button
                                            onClick={() => setStaged(new Map())}
                                            title="Tout annuler"
                                            className="ml-auto text-gray-400 hover:text-red-500"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                    <div className="max-h-56 overflow-y-auto divide-y divide-gray-100">
                                        {stagedList.slice(0, PREVIEW_CAP).map(([id, s]) => (
                                            <div key={id} className="px-2.5 py-1.5 flex items-start gap-1.5">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-[10px] text-gray-400 line-through truncate">
                                                        {s.from || "(sans nom)"}
                                                    </p>
                                                    <p className="text-[11px] text-gray-900 font-medium truncate">
                                                        {s.to}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => unstage(id)}
                                                    className="text-gray-300 hover:text-red-500 shrink-0"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}
                                        {stagedList.length > PREVIEW_CAP && (
                                            <p className="px-2.5 py-1.5 text-[10px] text-gray-400">
                                                … et {(stagedList.length - PREVIEW_CAP).toLocaleString("fr-FR")} autre
                                                {stagedList.length - PREVIEW_CAP > 1 ? "s" : ""}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Events carry the detected name; realigning them keeps the BO
                                events list from showing the code we just removed. */}
                            <label className="flex items-start gap-2 mb-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={renameEvents}
                                    onChange={e => setRenameEvents(e.target.checked)}
                                    className="w-3.5 h-3.5 mt-0.5 rounded border-gray-300 accent-black cursor-pointer shrink-0"
                                />
                                <span className="text-[11px] text-gray-600 leading-snug">
                                    Renommer aussi les events liés
                                    <span className="block text-[10px] text-gray-400">
                                        Uniquement ceux portant le SIRET de l'établissement, jamais ceux d'un
                                        établissement frère.
                                    </span>
                                </span>
                            </label>

                            <button
                                onClick={save}
                                disabled={staged.size === 0 || saving}
                                className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-sm text-xs font-semibold transition-all ${
                                    staged.size > 0 && !saving
                                        ? "bg-black text-white hover:bg-gray-800"
                                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                                }`}
                            >
                                {saving ? (
                                    <>
                                        <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        {saveProgress && saveProgress.total > RENAME_CHUNK
                                            ? `Enregistrement ${saveProgress.done}/${saveProgress.total}…`
                                            : "Enregistrement…"}
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-3.5 h-3.5" />
                                        {staged.size > 0
                                            ? `Enregistrer ${staged.size} renommage${staged.size > 1 ? "s" : ""}`
                                            : "Aucun renommage préparé"}
                                    </>
                                )}
                            </button>

                            {saveResult && (
                                <div className="mt-3 flex items-start gap-2 p-2.5 bg-green-50 border border-green-200 rounded-sm">
                                    <Check className="w-3.5 h-3.5 text-green-600 shrink-0 mt-0.5" />
                                    <p className="text-xs text-green-700 font-medium flex-1">
                                        {saveResult.modified.toLocaleString("fr-FR")} établissement
                                        {saveResult.modified > 1 ? "s" : ""} renommé
                                        {saveResult.modified > 1 ? "s" : ""}
                                        {renameEvents && (
                                            <span className="block text-[10px] text-green-600 font-normal mt-0.5">
                                                {saveResult.events.toLocaleString("fr-FR")} event
                                                {saveResult.events > 1 ? "s" : ""} réaligné
                                                {saveResult.events > 1 ? "s" : ""}
                                            </span>
                                        )}
                                        {saveResult.rejected > 0 && (
                                            <span className="block text-[10px] text-green-600 font-normal mt-0.5">
                                                {saveResult.rejected} refusé(s) : nom vide, trop court ou purement
                                                numérique
                                            </span>
                                        )}
                                    </p>
                                    <button
                                        onClick={() => setSaveResult(null)}
                                        className="text-green-500 hover:text-green-700"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
